import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FaArrowRight, FaShoppingCart, FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaTruck, FaCreditCard, FaCheck, FaClock, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyBranchOrderDetails = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setTitle('تفاصيل الطلب');
        fetchOrderDetails();
    }, [setTitle, id]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token || userRole !== 'pharmacy_branch') {
                toast.error('غير مصرح لك بالوصول لهذه الصفحة');
                navigate('/');
                return;
            }

            const response = await axios.get(`${BASE_URL}/branch/orders/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 200) {
                setOrder(response.data.data.data);
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الطلب');
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الطلب');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return <span className="badge bg-success">مكتمل</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'placed':
                return <span className="badge bg-info">تم الطلب</span>;
            case 'cancelled':
                return <span className="badge bg-danger">ملغي</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const getPaymentStatusBadge = (status) => {
        switch (status) {
            case 'paid':
                return <span className="badge bg-success">مدفوع</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'failed':
                return <span className="badge bg-danger">فشل</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const getDeliveryStatusBadge = (status) => {
        switch (status) {
            case 'delivered':
                return <span className="badge bg-success">تم التسليم</span>;
            case 'accepted':
                return <span className="badge bg-info">مقبول</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'cancelled':
                return <span className="badge bg-danger">ملغي</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-SA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            calendar: 'gregory'
        });
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="alert alert-warning text-center">
                        <h4>خطأ</h4>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/pharmacy-branch-orders')}
                        >
                            العودة للأوردرات
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="alert alert-warning text-center">
                        <h4>الطلب غير موجود</h4>
                        <p>لم يتم العثور على الطلب المطلوب.</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/pharmacy-branch-orders')}
                        >
                            العودة للأوردرات
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4" style={{gap : "10px"}}>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/pharmacy-branch-orders')}
                    >
                        <FaArrowRight className="mx-2" />
                        العودة للأوردرات
                    </motion.button>
                    
                    <motion.h2
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mb-0"
                        style={{ color: '#184194', fontSize: '20px' }}
                    >
                        تفاصيل الطلب #{order.id}
                    </motion.h2>
                </div>

                <div className="row">
                    {/* Order Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="col-lg-8 mb-4"
                    >
                        <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white border-0 py-3">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <FaShoppingCart className="mx-2" style={{ color: '#184194' }} />
                                    معلومات الطلب
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <h6 className="text-muted">رقم الطلب</h6>
                                        <p className="fw-medium">#{order.id}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <h6 className="text-muted">تاريخ الطلب</h6>
                                        <p className="fw-medium">{formatDate(order.created_at)}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <h6 className="text-muted">طريقة التسليم</h6>
                                        <p className="fw-medium">
                                            {order.delivery_method === 'delivery' ? 'توصيل' : 'استلام من الفرع'}
                                        </p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <h6 className="text-muted">طريقة الدفع</h6>
                                        <p className="fw-medium">
                                            {order.payment_method === 'cod' ? 'الدفع عند الاستلام' : order.payment_method}
                                        </p>
                                    </div>
                                    {order.notes && (
                                        <div className="col-12 mb-3">
                                            <h6 className="text-muted">ملاحظات</h6>
                                            <p className="fw-medium">{order.notes}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Order Items */}
                        <div className="card shadow-sm mt-4" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white border-0 py-3">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <FaShoppingCart className="mx-2" style={{ color: '#184194' }} />
                                    منتجات الطلب
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th className="border-0 py-3 px-4">المنتج</th>
                                                <th className="border-0 py-3 px-4">الملاحظات</th>
                                                <th className="border-0 py-3 px-4 text-center">الكمية</th>
                                                <th className="border-0 py-3 px-4 text-center">السعر</th>
                                                <th className="border-0 py-3 px-4 text-center">المجموع</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order.items.map((item, index) => (
                                                <tr key={item.id} className="border-bottom">
                                                    <td className="py-3 px-4">
                                                        <div className="d-flex align-items-center">
                                                            {item.meta.product_image && (
                                                                <img 
                                                                    src={item.meta.product_image} 
                                                                    alt={item.meta.product_name}
                                                                    className="mx-3"
                                                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                                                />
                                                            )}
                                                            <div>
                                                                <div className="fw-medium">{item.meta.product_name}</div>
                                                                <small className="text-muted">ID: {item.product_id}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        {item.meta.note ? (
                                                            <span className="text-muted">{item.meta.note}</span>
                                                        ) : (
                                                            <span className="text-muted">لا توجد ملاحظات</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <span className="fw-medium">{item.quantity}</span>
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <span className="fw-medium">{item.price} ج.م</span>
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <span className="fw-medium" style={{ color: '#28a745' }}>
                                                            {item.line_total} ج.م
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Sidebar */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="col-lg-4"
                    >
                        {/* Customer Info */}
                        <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white border-0 py-3">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <FaUser className="mx-2" style={{ color: '#184194' }} />
                                    معلومات العميل
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <h6 className="text-muted">الاسم</h6>
                                    <p className="fw-medium">{order.client.name}</p>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">رقم الهاتف</h6>
                                    <p className="fw-medium d-flex align-items-center">
                                        <FaPhone className="mx-2" style={{ fontSize: '12px' }} />
                                        {order.client.phone}
                                    </p>
                                </div>
                                {order.client.email && (
                                    <div className="mb-3">
                                        <h6 className="text-muted">البريد الإلكتروني</h6>
                                        <p className="fw-medium d-flex align-items-center">
                                            <FaEnvelope className="mx-2" style={{ fontSize: '12px' }} />
                                            {order.client.email}
                                        </p>
                                    </div>
                                )}
                                {order.client.address && (
                                    <div className="mb-3">
                                        <h6 className="text-muted">العنوان</h6>
                                        <p className="fw-medium d-flex align-items-start">
                                            <FaMapMarkerAlt className="mx-2 mt-1" style={{ fontSize: '12px' }} />
                                            <span style={{ fontSize: '13px', lineHeight: '1.4' }}>
                                                {order.client.address}
                                            </span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Order Status */}
                        <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white border-0 py-3">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <FaCheck className="mx-2" style={{ color: '#184194' }} />
                                    حالة الطلب
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <h6 className="text-muted">الحالة العامة</h6>
                                    <div className="d-flex align-items-center">
                                        {getStatusBadge(order.status)}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">حالة الدفع</h6>
                                    <div className="d-flex align-items-center">
                                        <FaCreditCard className="mx-2" style={{ fontSize: '12px' }} />
                                        {getPaymentStatusBadge(order.payment_status)}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">حالة التسليم</h6>
                                    <div className="d-flex align-items-center">
                                        <FaTruck className="mx-2" style={{ fontSize: '12px' }} />
                                        {getDeliveryStatusBadge(order.delivery_status)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white border-0 py-3">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <FaShoppingCart className="mx-2" style={{ color: '#184194' }} />
                                    ملخص الطلب
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>المجموع الفرعي:</span>
                                    <span className="fw-medium">{order.subtotal} ج.م</span>
                                </div>
                                {order.discount_total > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>الخصم:</span>
                                        <span className="fw-medium text-success">-{order.discount_total} ج.م</span>
                                    </div>
                                )}
                                {order.delivery_fees > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>رسوم التوصيل:</span>
                                        <span className="fw-medium">{order.delivery_fees} ج.م</span>
                                    </div>
                                )}
                                {order.extra_fees > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>رسوم إضافية:</span>
                                        <span className="fw-medium">{order.extra_fees} ج.م</span>
                                    </div>
                                )}
                                <hr />
                                <div className="d-flex justify-content-between">
                                    <span className="fw-bold">المجموع الكلي:</span>
                                    <span className="fw-bold fs-5" style={{ color: '#28a745' }}>
                                        {order.total} ج.م
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default PharmacyBranchOrderDetails;
